/**
 * Create-menu glyphs (稿 `93:7592`). Each drawing keeps the pixel size it had
 * inside a 24 slot; `<Icon size>` scales that slot. Primary strokes are
 * `currentColor`, accent strokes are `--app-color-accent`. A command names one
 * of these ids in its manifest `icon`; this file does not know command ids.
 *
 * New Chat is two layers. The bubble carries the稿's `rotate-180` + `scaleY(-1)`,
 * which together are a horizontal flip, and the plus sits at a fixed slot
 * offset so it does not flip with the bubble.
 *
 * Inner SVGs carry `size-` and `text-` in their class so a menu item's default
 * `[&_svg:not([class*='size-'])]` / `text-` rules do not restyle them.
 */
import type { ComponentType, ReactNode, SVGProps } from 'react';

import { cn } from '../../lib/utils';
import CreateAiChat from './create-ai-chat.svg?react';
import CreateApp from './create-app.svg?react';
import CreateBot from './create-bot.svg?react';
import CreateChat from './create-chat.svg?react';
import CreateChatPlus from './create-chat-plus.svg?react';
import CreateContact from './create-contact.svg?react';
import CreateGroup from './create-group.svg?react';
import CreateWorkspace from './create-workspace.svg?react';

type GlyphComponent = ComponentType<{ size?: number; className?: string }>;

const SLOT = 24;

const GLYPH_CLASS = 'size-auto shrink-0 text-current';

function fittedGlyph(
  Glyph: ComponentType<SVGProps<SVGSVGElement>>,
  width: number,
  height: number,
): GlyphComponent {
  function FittedGlyph({
    size = SLOT,
    className,
  }: {
    size?: number;
    className?: string;
  }): ReactNode {
    const scale = size / SLOT;
    return (
      <span
        aria-hidden
        className={cn(
          'inline-flex shrink-0 items-center justify-center',
          className,
        )}
        style={{ width: size, height: size }}
      >
        <Glyph
          className={GLYPH_CLASS}
          style={{
            display: 'block',
            width: width * scale,
            height: height * scale,
          }}
        />
      </span>
    );
  }
  return FittedGlyph;
}

export const CreateWorkspaceIcon = fittedGlyph(CreateWorkspace, 24, 24);
export const CreateAiChatIcon = fittedGlyph(CreateAiChat, 19.9, 19.9552);
export const CreateGroupIcon = fittedGlyph(CreateGroup, 19, 21.3101);
export const CreateContactIcon = fittedGlyph(CreateContact, 23, 23);
export const CreateBotIcon = fittedGlyph(CreateBot, 21.4819, 21.9028);
export const CreateAppIcon = fittedGlyph(CreateApp, 18.7917, 18.7917);

export function CreateChatIcon({
  size = SLOT,
  className,
}: {
  size?: number;
  className?: string;
}): ReactNode {
  const scale = size / SLOT;
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center',
        className,
      )}
      style={{ width: size, height: size }}
    >
      <CreateChat
        className={cn('-scale-x-100', GLYPH_CLASS)}
        style={{
          display: 'block',
          width: 19.8311 * scale,
          height: 19.9468 * scale,
        }}
      />
      <CreateChatPlus
        className={cn('absolute', GLYPH_CLASS)}
        style={{
          display: 'block',
          width: 6.96308 * scale,
          height: 6.99512 * scale,
          left: 14.989 * scale,
          top: 12.87 * scale,
        }}
      />
    </span>
  );
}
